import styled from '@emotion/styled';
import type { Prismic } from '@landsbankinn/prismic';
import { useFocusMethod } from '@landsbankinn/utils';
import React from 'react';
import { useLayout } from 'hooks/useLayout';
import { Navigation } from '../navigation/Navigation';

interface Navigation {
  navigation_item: {
    _meta: {
      id: string;
    };
  };
  navigation_title: any;
}

interface Card {
  card_heading: Prismic.RichText;
  card_copy: Prismic.RichText;
  card_image: any;
  card_link: any;
}

interface Website {
  title: string;
  navigation: Navigation[];
  cards: Card[];
}

interface AppLayoutProps {
  children: React.ReactNode;
  page: any & {
    website: Website;
    preview: boolean;
    previewData?: { ref: string };
  };
  activeIds: string[];
}

const Main = styled.div`
  position: relative;
  min-height: 100vh;
  /* Can't have this because it brakes position stikcy */
  /* overflow-x: hidden; */
  /* @todo Fix for jumping navbar */
  &:before {
    content: '';
    display: block;
    height: ${(props) => props.theme.headerHeight}px;
    position: absolute;
    z-index: 1;
    top: 0;
    right: 0;
    left: 0;
    width: 100%;
    background: ${(props) => props.theme.colors.primary};
  }
`;

const ExitPreview = styled.div`
  position: fixed;
  bottom: 28px;
  padding: 4px 16px;
  border-radius: 6px;
  background-color: #fff;
  text-align: center;
  z-index: 1000;
  right: 96px;
  box-shadow: 0 11px 40px 0 rgba(0, 0, 0, 0.25),
    0 2px 10px 0 rgba(0, 0, 0, 0.12);
  > a {
    font-family: sans-serif;
    font-size: 15px;
    font-weight: bold;
    text-decoration: none;
    color: #222;
  }
`;

export const AppLayout = ({ children, page, activeIds }: AppLayoutProps) => {
  useFocusMethod();
  const ref = useLayout();
  const { preview, website } = page;

  const lang = (website?._meta?.lang ?? 'is').split('-').shift();
  const prefix = `/${lang}`.replace('/is', '');
  const pathname = (children as any)?.[0]?.props?.router?.pathname;
  const isFrontPage = page?.page?.uid === 'index';
  const isSearchPage = [`${prefix}/leit`, `${prefix}/search`].includes(
    pathname,
  );

  return (
    // id is used for ally purposes
    <Main ref={ref} id="main">
      {preview && (
        <ExitPreview>
          <a href="/api/exit-preview">Exit preview</a>
        </ExitPreview>
      )}
      <Navigation
        title={website?.title}
        page={page?.page}
        navigation={website?.navigation}
        lang={website?._meta?.lang}
        quicklinks={website?.quicklinks}
        openingHours={website?.opening_hours}
        cards={website?.cards}
        bottomLinks={website?.bottom_links}
        activeIds={activeIds}
        isTransparent={isFrontPage || isSearchPage}
      />
      <>{children}</>
    </Main>
  );
};
